import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocalizedNav } from "@/i18n/useLocalizedNav";
import { useLocalizedHref } from "@/i18n/useLocalizedHref";
import { detectLocale } from "@/i18n/routeMap";
import { comingSoonAreas, serviceAreaLabels } from "@/content/service-areas";
import { Button } from "./Button";
import wordmark from "@/assets/fenny-wordmark-butter.svg";

type Props = {
  open: boolean;
  onClose: () => void;
};

/**
 * Fenny mobile menu — fokuserad, kollapserbar editorial-meny.
 * Toppnivå alltid synlig, undermenyer expanderar på tap.
 * Affärsområden (Film, Foto, 3D) markeras med egen chip så de syns tydligt.
 */
export function MobileMenu({ open, onClose }: Props) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const panelRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const nav = useLocalizedNav();
  const loc = useLocalizedHref();
  const lang = detectLocale(pathname) === "en" ? "en" : "sv";
  const areaLabels = serviceAreaLabels(lang);
  const soon = comingSoonAreas(lang);
  const contactHref = loc("/kontakt");

  const isActive = (to: string) =>
    pathname === to || (to !== "/" && pathname.startsWith(to + "/"));


  // Body scroll lock + ESC + focus + auto-expand matching group
  useEffect(() => {
    if (!open) {
      setExpanded(null);
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Auto-expand the group matching current route
    const match = nav.find(
      (e) =>
        e.kind === "mega" &&
        e.activeWhen.some(
          (p: string) => pathname === p || pathname.startsWith(p + "/"),
        ),
    );
    if (match) setExpanded(match.label);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const t = window.setTimeout(() => {
      panelRef.current?.querySelector<HTMLElement>("[data-menu-first]")?.focus();
    }, 250);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose]);

  // Close on route change (men inte vid första monteringen — menyn monteras
  // först när den öppnas, och då ska den förstås förbli öppen)
  const lastPath = useRef(pathname);
  useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Mobilmenyns fulla innehåll är tungt och behövs aldrig vid första paint.
  // Vi monterar det först när menyn öppnats en gång — sparar ~tiotals KB HTML
  // per sidvisning utan att påverka länkarna som redan finns i footern.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);
  if (!mounted) return null;

  return (
    <div
      id="mobile-menu"
      className={cn(
        "fixed inset-0 z-[60] lg:hidden",
        open ? "pointer-events-auto" : "pointer-events-none",
      )}
      aria-hidden={!open}
    >
      {/* Scrim */}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        onClick={onClose}
        className={cn(
          "absolute inset-0 bg-black/70 backdrop-blur-md transition-opacity duration-300",
          open ? "opacity-100" : "opacity-0",
        )}
      />

      {/* Sheet */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("nav.menu")}
        className={cn(
          "absolute inset-x-0 top-0 flex h-dvh flex-col overflow-hidden bg-background text-foreground transition-[transform,opacity] duration-[420ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
          open ? "translate-y-0 opacity-100" : "-translate-y-3 opacity-0",
        )}
      >
        {/* Mint glow */}
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute -top-40 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-mint/25 blur-3xl transition-opacity duration-700",
            open ? "opacity-100" : "opacity-0",
          )}
        />

        {/* Header row: F-mark + close */}
        <div className="relative flex items-center justify-between px-5 pt-[max(env(safe-area-inset-top),1rem)] pb-2">
          <img src={wordmark} alt="Fenny" className="h-5 w-auto" />
          <button
            type="button"
            onClick={onClose}
            aria-label={t("nav.closeMenu")}
            className="-mr-2 inline-flex h-11 w-11 items-center justify-center rounded-full text-foreground/80 transition-colors hover:bg-white/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint/60"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Editorial index */}
        <nav
          aria-label={t("nav.mobileNav")}
          className="relative flex-1 overflow-y-auto overscroll-contain px-5 pb-4 pt-2"
        >
          <ul className="flex flex-col">
            {nav.map((entry, i) => {
              const numLabel = String(i + 1).padStart(2, "0");

              if (entry.kind === "link") {
                const active = isActive(entry.to);
                return (
                  <li
                    key={entry.label}
                    className={cn(
                      "border-b border-white/[0.06] transition-all duration-500",
                      open ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0",
                    )}
                    style={{ transitionDelay: open ? `${120 + i * 70}ms` : "0ms" }}
                  >
                    <Link
                      to={entry.to}
                      data-menu-first={i === 0 ? "" : undefined}
                      onClick={onClose}
                      className="group flex items-center gap-4 py-4"
                    >
                      <span className="font-mono text-[11px] tabular-nums text-white/35">
                        {numLabel}
                      </span>
                      <span
                        className={cn(
                          "flex-1 font-serif text-[30px] leading-none tracking-[-0.02em] transition-colors",

                          active ? "text-mint" : "text-foreground group-hover:text-mint",
                        )}
                      >
                        {entry.label}
                      </span>
                      <ArrowUpRight
                        className="h-5 w-5 text-white/40 transition-all group-hover:text-mint"
                        aria-hidden
                      />
                    </Link>
                  </li>
                );
              }

              // Panel entry — accordion
              const isOpenGroup = expanded === entry.label;
              const groupActive = entry.activeWhen.some(
                (p) => pathname === p || pathname.startsWith(p + "/"),
              );
              const panelId = `mm-panel-${i}`;
              return (
                <li
                  key={entry.label}
                  className={cn(
                    "border-b border-white/[0.06] transition-all duration-500",
                    open ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0",
                  )}
                  style={{ transitionDelay: open ? `${120 + i * 70}ms` : "0ms" }}
                >
                  <button
                    type="button"
                    data-menu-first={i === 0 ? "" : undefined}
                    onClick={() => setExpanded(isOpenGroup ? null : entry.label)}
                    aria-expanded={isOpenGroup}
                    aria-controls={panelId}
                    className="group flex w-full items-center gap-4 py-4 text-left"
                  >
                    <span className="font-mono text-[11px] tabular-nums text-white/35">
                      {numLabel}
                    </span>
                    <span className="flex min-w-0 flex-1 items-center gap-2">
                      <span
                        className={cn(
                          "font-serif text-[30px] leading-none tracking-[-0.02em] transition-colors",
                          groupActive || isOpenGroup
                            ? "text-mint"
                            : "text-foreground group-hover:text-mint",
                        )}
                      >
                        {entry.label}
                      </span>
                    </span>
                    <ChevronDown
                      className={cn(
                        "h-5 w-5 shrink-0 text-white/40 transition-transform duration-300",
                        isOpenGroup && "rotate-180 text-mint",
                      )}
                      aria-hidden
                    />
                  </button>

                  {/* Collapsible content via grid-rows trick */}
                  <div
                    id={panelId}
                    className={cn(
                      "grid transition-[grid-template-rows,opacity] duration-[360ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
                      isOpenGroup
                        ? "grid-rows-[1fr] opacity-100"
                        : "grid-rows-[0fr] opacity-0",
                    )}
                  >
                    <div className="overflow-hidden">
                      <div className="pb-4 pl-9 pr-1">
                        {entry.columns.map((col, ci) => (
                          <div key={ci} className="mb-4 last:mb-0">
                            {col.eyebrow.trim() && (
                              <div className="mb-1.5 text-[10px] uppercase tracking-[0.18em] text-mint/70">
                                {col.eyebrow}
                              </div>
                            )}
                            <ul className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                              {col.items.map((item) => {
                                const active = isActive(item.to as string);
                                return (
                                  <li key={`${item.to}-${item.params?.slug ?? item.label}`}>
                                    <Link
                                      to={item.to as string}
                                      params={item.params as never}
                                      onClick={onClose}
                                      className={cn(
                                        "flex min-h-[40px] items-center gap-1.5 py-1.5 text-[14.5px] font-medium leading-tight transition-colors",
                                        active
                                          ? "text-mint"
                                          : "text-white/80 hover:text-foreground",
                                      )}
                                    >
                                      {active && (
                                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-mint" />
                                      )}
                                      <span className="break-words">{item.label}</span>
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                </li>
              );
            })}

            {/* Affärsområden på väg — t.ex. 3D */}
            {soon.map((area) => (
              <li
                key={area.id}
                className={cn(
                  "border-b border-white/[0.06] transition-all duration-500",
                  open ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0",
                )}
                style={{ transitionDelay: open ? `${120 + nav.length * 70}ms` : "0ms" }}
              >
                <div className="flex items-center gap-4 py-4">
                  <span className="font-mono text-[11px] tabular-nums text-white/35">
                    {String(nav.length + 1).padStart(2, "0")}
                  </span>
                  <span className="flex min-w-0 flex-1 items-center gap-2">
                    <span className="font-serif text-[30px] leading-none tracking-[-0.02em] text-white/40">
                      {area.label}
                    </span>
                    <span className="rounded-full border border-white/20 px-2 py-[3px] text-[9px] uppercase tracking-[0.16em] text-white/50">
                      {area.note}
                    </span>
                  </span>
                </div>
              </li>
            ))}



            {/* Kontakt */}
            <li
              className={cn(
                "transition-all duration-500",
                open ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0",
              )}
              style={{ transitionDelay: open ? `${120 + nav.length * 70}ms` : "0ms" }}
            >
              <Link
                to={contactHref as string}
                onClick={onClose}
                className="group flex items-center gap-4 py-5"
              >
                <span className="font-mono text-[11px] tabular-nums text-white/35">
                  {String(nav.length + soon.length + 1).padStart(2, "0")}
                </span>
                <span
                  className={cn(
                    "flex-1 font-serif text-[34px] leading-none tracking-[-0.02em] transition-colors",
                    isActive(contactHref)
                      ? "text-mint"
                      : "text-foreground group-hover:text-mint",
                  )}
                >
                  {t("nav.contact")}
                </span>
                <ArrowUpRight
                  className="h-5 w-5 text-white/40 transition-all group-hover:text-mint"
                  aria-hidden
                />
              </Link>
            </li>
          </ul>
        </nav>

        {/* Sticky CTA */}
        <div
          className={cn(
            "relative border-t border-white/10 bg-background/95 px-5 pb-[max(env(safe-area-inset-bottom),1rem)] pt-3 backdrop-blur-md transition-all duration-500",
            open ? "translate-y-0 opacity-100 delay-[260ms]" : "translate-y-3 opacity-0",
          )}
        >
          <Button
            to={contactHref}
            variant="peach"
            arrow
            className="w-full justify-center text-[15px]"
            trackId="contact_us"
            trackProps={{ location: "header_mobile" }}
            onClick={onClose}
          >
            {t("nav.contact")}
          </Button>
        </div>
      </div>
    </div>
  );
}
